import React, { useState, FC, useContext } from 'react';

import { Icon, Paper, Grid } from '@material-ui/core';
import { navigate } from '@reach/router';
import CopyToClipboard from 'react-copy-to-clipboard';

import { GlobalContext } from '../../../../store';
import { IAdminEvent } from '../../../../store/admin/events/store';

import Button from '../../../../components/Button';
import { lazyEqual } from '../../../../contrib/utils';
import { EventWizardStepTitle } from '../Components/EventWizardStepTitle/EventWizardStepTitle';
import { getEventURL } from '../../../../contrib/Utils/getEventURL';
import { useGetAdminEvent } from '../../../../contrib/Hooks/useGetAdminEvent';

interface IProps {
  clubId: string;
  eventId: string;
}

const EventPublishComponent: FC<IProps> = props => {
  const { clubId, eventId } = props;

  const { actions } = useContext(GlobalContext);

  const event = useGetAdminEvent(eventId);

  const [copySuccess, setCopySuccess] = useState(false);

  const forward = () => navigate(`/staff/club/${clubId}/event/${eventId}/setup/players`);
  const exit = () => navigate(`/staff/club/${clubId}`);

  const publishEvent = async (): Promise<any> => {
    if (event) {
      const newEvent: IAdminEvent = {
        ...event,
        status: 'published'
      };
      await save(newEvent);
    }
  };

  const cancelEvent = async (): Promise<any> => {
    if (event) {
      const newEvent: IAdminEvent = {
        ...event,
        status: 'draft'
      };
      await save(newEvent);
    }
  };

  const save = async (newEvent: Partial<IAdminEvent>, noAdvance?: boolean) => {
    if (lazyEqual(event, newEvent)) {
      if (noAdvance) {
        exit();
      } else {
        forward();
      }
      return;
    }

    const response = await actions.adminEvents.edit(Number(clubId), newEvent);

    if (response.adminEvents && response.adminEvents.current) {
      const current = response.adminEvents.current;
      if (current.rounds.length) {
        for (const round of current.rounds) {
          actions.adminRoundFormats.get(Number(clubId), current.id, round.id);
        }
      }
    }
    if (!response.errors) {
      // TODO: Fix errors and edge condition
      if (noAdvance) {
        exit();
      } else {
        forward();
      }
    } else if (response.errors) {
      await actions.notifications.push({
        status: 'unread',
        text: response.errors.events.msg || '',
        type: 'error'
      });
    }
  };
  const saveAndExit = async () => {
    if (event) {
      await save(event, true);
    }
  };

  const onCopy = (): void => {
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
    }, 5 * 1000);
  };

  const copyText = getEventURL(eventId);

  return (
    <div className="event-wizard-page form publish-event">
      <EventWizardStepTitle title="PUBLISH EVENT" buttonText="CONTINUE" buttonOnClick={forward} />

      <Paper elevation={1} className="paper-publish-event override">
        <div className="paper-item">
          <h3 className="title">Publish Event</h3>

          <p className="description">
            Once an event is published players can be invited and register for the event. Don’t
            worry if something in the event changes, settings can always be updated and players will
            be notified of the changes. Publishing will also unlock the “Round 1” tab in the red
            tool bar above where modifications to handicaps, flights, and the tee sheet.
          </p>

          <Grid className="paper-item-buttons" container={true} spacing={4}>
            <Grid item={true} xs={6} sm={4} md={3}>
              <Button
                classes={{ label: 'label' }}
                className="button -contained override publishEventButton"
                variant="contained"
                color="primary"
                type="button"
                fullWidth={true}
                onClick={publishEvent}
              >
                Publish Event
              </Button>
            </Grid>

            <Grid item={true} xs={6} sm={4} md={3}>
              <Button
                classes={{ label: 'label' }}
                className="button override saveAndExitButton"
                variant="outlined"
                color="primary"
                type="button"
                fullWidth={true}
                onClick={saveAndExit}
              >
                Save & Exit
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className="paper-item">
          <h3 className="title">Unique link to event</h3>

          <p className="description">
            After the event is published this link will allow any player qualifed to play in this
            event access to register.
          </p>

          <div className="copy-widget">
            <CopyToClipboard text={copyText} onCopy={onCopy}>
              <div className="copy-widget-container">
                <div className="text">
                  <span>{copyText}</span>
                </div>

                <div className="copy-widget-cta">
                  <span className="message">{copySuccess ? 'Copied' : 'Copy Link'}</span>

                  <Icon>filter_none</Icon>
                </div>
              </div>
            </CopyToClipboard>
          </div>
        </div>

        <div className="paper-item" style={{ display: 'none' }}>
          <h3 className="title">Launching Events and Rounds </h3>

          <p className="description">
            After an event is published players will be notified that they can register. As the
            event date gets closer the next step will be to create the tee sheet for the first
            round. The “Round 1” tab in the red tool bar
          </p>
        </div>

        <div className="paper-item">
          <h3 className="title">Cancel event</h3>

          <p className="description">
            Canceling the event will remove it from a players history. You can cancel an event at
            any time. Once an event is canceled it cannot be recovered.
          </p>

          <Grid className="paper-item-buttons" container={true} spacing={4}>
            <Grid item={true} xs={6} sm={4} md={3}>
              <Button
                classes={{ label: 'label' }}
                className="button override cancelButton"
                variant="outlined"
                color="primary"
                type="button"
                fullWidth={false}
                onClick={cancelEvent}
              >
                Cancel Event
              </Button>
            </Grid>
          </Grid>
        </div>
      </Paper>
    </div>
  );
};

export default EventPublishComponent;
